// BYOC (Bring Your Own Cloud) Firestore helpers
//
// Schema: /orgs/{orgId}/byoc/{provider}
//   provider: 'aws' | 'gcp' | 'azure'
//
// All fields are non-secret identifiers — role-based auth means no keys are stored.
// CrimsonScan assumes/federates into the customer's cloud using the stored identifiers.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { firestoreDb } from '../config/firebase';

const db = firestoreDb;

// ── External ID generation ────────────────────────────────────────────────────
// AWS requires a unique External ID per org to prevent confused deputy attacks.
// Generated once and stored on the org's byoc/aws doc.

export function generateExternalId() {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── CRUD ──────────────────────────────────────────────────────────────────────

/**
 * Save BYOC config for a provider.
 * Fields vary by provider — only pass what's relevant.
 */
export async function saveByocConfig(orgId, provider, config) {
  if (!db || !orgId || !provider) return;
  await setDoc(
    doc(db, 'orgs', orgId, 'byoc', provider),
    { ...config, provider, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/**
 * Load BYOC config for a specific provider.
 */
export async function getByocConfig(orgId, provider) {
  if (!db || !orgId || !provider) return null;
  const snap = await getDoc(doc(db, 'orgs', orgId, 'byoc', provider));
  return snap.exists() ? snap.data() : null;
}

/**
 * Load all configured BYOC providers for an org.
 * Returns { aws: {...} | null, gcp: {...} | null, azure: {...} | null }
 */
export async function getAllByocConfigs(orgId) {
  if (!db || !orgId) return { aws: null, gcp: null, azure: null };
  const snap = await getDocs(collection(db, 'orgs', orgId, 'byoc'));
  const result = { aws: null, gcp: null, azure: null };
  snap.docs.forEach(d => { result[d.id] = d.data(); });
  return result;
}

/**
 * Disable/remove a provider's BYOC config.
 */
export async function removeByocConfig(orgId, provider) {
  if (!db || !orgId || !provider) return;
  await deleteDoc(doc(db, 'orgs', orgId, 'byoc', provider));
}

// ── BYOC endpoint helpers ────────────────────────────────────────────────────

/**
 * Get active BYOC endpoints for an org (GCP only for now).
 * Returns { active: true, endpoints: { mattergen, dft, gt4sd } } or { active: false }.
 */
export async function getByocEndpoints(orgId) {
  if (!db || !orgId) return { active: false };
  const snap = await getDoc(doc(db, 'orgs', orgId, 'byoc', 'gcp'));
  if (!snap.exists()) return { active: false };
  const data = snap.data();
  if (!data.enabled || !data.endpoints) return { active: false };
  const endpoints = data.endpoints || {};
  // At least one endpoint must be configured
  const hasAny = Object.values(endpoints).some(v => v && typeof v === 'string' && v.trim());
  if (!hasAny) return { active: false };
  return { active: true, endpoints };
}

/**
 * Save health check results for a BYOC config.
 */
export async function saveByocHealthCheck(orgId, results) {
  if (!db || !orgId) return;
  await setDoc(
    doc(db, 'orgs', orgId, 'byoc', 'gcp'),
    { lastHealthCheck: { ts: serverTimestamp(), results } },
    { merge: true }
  );
}
